import React, { useCallback, useEffect, useState } from "react";
import axios from "../../../api";
import {
  Button,
  TextField,
  Grid,
  Box,
  Autocomplete,
  Modal,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";

const customScrollbarStyle = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#666" },
};

const validationSchema = Yup.object({
  spare_part_name: Yup.string().required("Spare Part name is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  sku: Yup.string().required("SKU is required"),
  price: Yup.number().required("Price is required"),
  quantity: Yup.number().required("Quantity is required"),
  availability_status: Yup.string().required("Availability is required"),
});

interface SparePartsProps {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => Promise<void>;
}

interface Assets {
  asset_type_id: number;
  asset_type_name: string;
}

const AddSparePart = ({ isOpen, onClose, fetchData }: SparePartsProps) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [assets, setAssets] = useState<Assets[]>([]);
  const [sparePartName, setSparePartName] = useState("");
  const [generatedSku, setGeneratedSku] = useState("");

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: {
          is_active: 1,
        },
      });
      const assetData =
        response.data.list && Array.isArray(response.data.list)
          ? response.data.list
          : [];

      setAssets(assetData);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [fetchError]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const generateSkuPreview = async (name: string) => {
    if (!name) return "";

    // Using replace with global flag is correct here since we're replacing all occurrences
    const prefix =
      name
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 3) || "SPP";
    try {
      const res = await axios.get(`/spare-parts/sku-preview/${prefix}`);
      return res.data.sku;
    } catch (err) {
      console.error("SKU preview error:", err);
      return `${prefix}-001`;
    }
  };

  // Generate SKU when spare part name changes
  useEffect(() => {
    const fetchSku = async () => {
      if (sparePartName) {
        const generated = await generateSkuPreview(sparePartName);
        setGeneratedSku(generated);
      } else {
        setGeneratedSku("");
      }
    };
    fetchSku();
  }, [sparePartName]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="otp-modal"
      aria-describedby="otp-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          minWidth: 500,
          minHeight: 220,
          maxHeight: "calc(100vh - 100px)",
          overflow: "auto",
          ...customScrollbarStyle,
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Stock
        </Typography>
        <Formik
          initialValues={{
            spare_part_name: "",
            asset_type_id: "",
            sku: "",
            price: "",
            quantity: "",
            availability_status: "In Stock",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            setSubmitting(true);
            try {
              const response = await axios.post("/spare-parts", values);
              fetchData();
              fetchSuccess(response.data.message);
              onClose();
            } catch (error) {
              if (error instanceof AxiosError && error.response) {
                fetchError(error.response.data.error);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue }) => {
            // Sync the generated SKU to the form
            if (generatedSku && values.sku !== generatedSku) {
              setFieldValue("sku", generatedSku);
            }

            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Spare Part Name
                      </SoftTypography>
                      <Field
                        name="spare_part_name"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        error={
                          !!errors.spare_part_name && touched.spare_part_name
                        }
                        helperText={
                          touched.spare_part_name && errors.spare_part_name
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          setFieldValue("spare_part_name", value);
                          setSparePartName(value);
                        }}
                      />
                    </SoftBox>
                  </Grid>

                  {/* Asset Type */}
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Asset Type
                    </SoftTypography>
                    <Autocomplete
                      options={assets}
                      getOptionLabel={(option) => option.asset_type_name || ""}
                      onChange={(e, value) =>
                        setFieldValue(
                          "asset_type_id",
                          value?.asset_type_id || ""
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={
                            !!errors.asset_type_id && touched.asset_type_id
                          }
                          helperText={
                            touched.asset_type_id && errors.asset_type_id
                          }
                        />
                      )}
                    />
                  </Grid>

                  {/* SKU (Auto-generated) */}
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        SKU (Auto-generated)
                      </SoftTypography>
                      <Field
                        name="sku"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        value={values.sku}
                        InputProps={{ readOnly: true }}
                        helperText="Auto-generated based on Spare Part Name"
                      />
                    </SoftBox>
                  </Grid>

                  {/* Price */}
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Price
                      </SoftTypography>
                      <Field
                        name="price"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        error={!!errors.price && touched.price}
                        helperText={touched.price && errors.price}
                      />
                    </SoftBox>
                  </Grid>

                  {/* Quantity */}
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Quantity
                      </SoftTypography>
                      <Field
                        name="quantity"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        error={!!errors.quantity && touched.quantity}
                        helperText={touched.quantity && errors.quantity}
                      />
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Availability Status
                      </SoftTypography>
                      <Field
                        name="availability_status"
                        as={TextField}
                        select
                        fullWidth
                        variant="outlined"
                        error={
                          !!errors.availability_status &&
                          touched.availability_status
                        }
                        helperText={
                          touched.availability_status &&
                          errors.availability_status
                        }
                      >
                        <MenuItem value="In Stock">In Stock</MenuItem>
                        <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                      </Field>
                    </SoftBox>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      className="success-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                      }}
                      variant="contained"
                      disabled={submitting}
                      type="submit"
                      color="success"
                    >
                      Save
                    </Button>
                    <Button
                      className="cancel-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                        ml: 2.5,
                      }}
                      variant="contained"
                      color="warning"
                      onClick={onClose}
                      type="button"
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddSparePart;
